import React, { useMemo, useState } from 'react';
import {
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircleOutline as ActivateIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { impersonate } from 'js/actions';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { showAlert } from 'new/stores/showAlert/action';
import { TextField } from 'new/elements/TextField';
import { ReactComponent as IncognitoIcon } from 'new/components/icons/incognito.svg';
import { ReactComponent as IncognitoOff } from 'new/components/icons/incognitoOff.svg';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useModals } from 'new/hooks/useModals';
import { AddUser } from 'new/components/settings/Members/AddUser';
import {
  useActivateAdminOrgMembers,
  useDeactivateAdminOrgMembers,
  useFetchAdminOrgMembers,
} from 'new/stores/adminOrgs/adminOrgs.api';
import { AdminOrg, OrgMember } from 'new/stores/adminOrgs/adminOrgs.types';
import { AdminRole } from '../../RoleBanner';
import { GuardedControl } from '../../GuardedControl';
import { ColumnFilterHeader } from '../../ColumnFilterHeader';
import { getMemberRoleLabel } from '../../utils';
import styles from './UsersTab.module.scss';

interface UsersTabProps {
  org: AdminOrg;
  role: AdminRole;
}

type MembershipFilter = 'ACTIVE' | 'DEACTIVATED' | 'ALL';
type RoleFilter = 'ALL' | 'Admin' | 'Collaborator' | 'Member';

const STATUS_OPTIONS: { value: MembershipFilter; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DEACTIVATED', label: 'Deactivated' },
  { value: 'ALL', label: 'All' },
];

const ROLE_OPTIONS: { value: RoleFilter; label: string }[] = [
  { value: 'ALL', label: 'All roles' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Collaborator', label: 'Collaborator' },
  { value: 'Member', label: 'Member' },
];

const ROLE_CHIP_COLOR: Record<string, 'primary' | 'default'> = {
  Admin: 'primary',
};

function UserRow({
  member,
  isSelf,
  disabledForWrite,
  onImpersonate,
  onToggleActive,
}: {
  member: OrgMember;
  isSelf: boolean;
  disabledForWrite: boolean;
  onImpersonate: (member: OrgMember) => void;
  onToggleActive: (member: OrgMember) => void;
}) {
  const isActive = member.org_membership_status === 'ACTIVE';
  const canImpersonate = isActive && !isSelf;
  const canToggleActive = !isSelf;

  return (
    <div className={styles.gridRow}>
      <Typography variant="body2" fontWeight={500} className={styles.cellTruncate}>
        {member.full_name}
      </Typography>
      <Typography variant="body3" color="text.secondary" className={styles.cellTruncate}>
        {member.email}
      </Typography>
      <Chip
        size="small"
        label={getMemberRoleLabel(member)}
        color={ROLE_CHIP_COLOR[getMemberRoleLabel(member)] ?? 'default'}
      />
      <Typography variant="body2" color={isActive ? 'text.primary' : 'text.secondary'}>
        {isActive ? 'Active' : 'Deactivated'}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" className={styles.iconCell}>
        <Tooltip
          title={
            isSelf
              ? 'Cannot impersonate the current user'
              : !isActive
                ? 'Cannot impersonate a deactivated user'
                : 'Impersonate this user'
          }
        >
          <span>
            <IconButton
              disabled={!canImpersonate}
              onClick={() => onImpersonate(member)}
              size="small"
            >
              {canImpersonate ? (
                <IncognitoIcon width={18} height={18} />
              ) : (
                <IncognitoOff width={18} height={18} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <GuardedControl
          disabled={disabledForWrite || !canToggleActive}
          tooltip={isSelf ? 'Cannot deactivate the current user' : undefined}
        >
          <IconButton
            onClick={() => onToggleActive(member)}
            size="small"
            color={isActive ? 'default' : 'success'}
            title={isActive ? 'Deactivate user' : 'Activate user'}
          >
            {isActive ? (
              <BlockIcon sx={{ fontSize: 18 }} />
            ) : (
              <ActivateIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </GuardedControl>
      </Stack>
    </div>
  );
}

export function UsersTab({ org, role }: UsersTabProps) {
  const { data: members = [], refetch: refetchMembers } = useFetchAdminOrgMembers(org.id);
  const currentUser = useCurrentUser();
  const dispatch = useAppDispatch();
  const { openModal } = useModals();
  const [activateMembers] = useActivateAdminOrgMembers();
  const [deactivateMembers] = useDeactivateAdminOrgMembers();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MembershipFilter>('ACTIVE');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');
  const disabledForWrite = role !== 'full';

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return members
      .filter((m) => roleFilter === 'ALL' || getMemberRoleLabel(m) === roleFilter)
      .filter((m) => statusFilter === 'ALL' || m.org_membership_status === statusFilter)
      .filter((m) => {
        if (!query) return true;
        const statusLabel = m.org_membership_status === 'ACTIVE' ? 'active' : 'deactivated';
        return (
          m.full_name?.toLowerCase().includes(query) ||
          m.email?.toLowerCase().includes(query) ||
          statusLabel.includes(query)
        );
      });
  }, [members, roleFilter, statusFilter, search]);

  const onImpersonate = (member: OrgMember) => {
    dispatch(impersonate(member.id, org.id));
  };

  const onToggleActive = (member: OrgMember) => {
    const isActive = member.org_membership_status === 'ACTIVE';
    const mutate = isActive ? deactivateMembers : activateMembers;
    mutate({ orgId: org.id, members: [{ id: member.id }] }).then((result) => {
      if ('error' in result) {
        dispatch(
          showAlert({
            title: `Couldn't ${isActive ? 'deactivate' : 'activate'} ${member.email}`,
            severity: 'error',
          }),
        );
        return;
      }
      dispatch(
        showAlert({
          title: `${member.email} ${isActive ? 'deactivated' : 'activated'}`,
          severity: 'success',
        }),
      );
    });
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1">Users</Typography>
          <Chip size="small" label={`${members.length} members`} />
        </Stack>
        <GuardedControl disabled={disabledForWrite}>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() =>
              openModal(AddUser.modalConfig.name, {
                orgId: org.id,
                orgName: org.name,
                onSuccess: refetchMembers,
              })
            }
          >
            Add user
          </Button>
        </GuardedControl>
      </div>

      <TextField
        className={styles.search}
        placeholder="Find users by name, email, or status"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <div className={styles.grid}>
        <div className={styles.headerRow}>
          <span className={styles.headerCell}>Name</span>
          <span className={styles.headerCell}>Email</span>
          <ColumnFilterHeader
            label="Role"
            options={ROLE_OPTIONS}
            value={roleFilter}
            defaultValue="ALL"
            onChange={setRoleFilter}
          />
          <ColumnFilterHeader
            label="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            defaultValue="ACTIVE"
            onChange={setStatusFilter}
          />
          <span />
        </div>
        {filteredMembers.map((member) => (
          <UserRow
            key={member.id}
            member={member}
            isSelf={member.email === currentUser?.email}
            disabledForWrite={disabledForWrite}
            onImpersonate={onImpersonate}
            onToggleActive={onToggleActive}
          />
        ))}
        {!filteredMembers.length && (
          <Typography variant="body2" color="text.secondary" className={styles.emptyState}>
            No users match your filters.
          </Typography>
        )}
      </div>
    </div>
  );
}
